"use client";

import { useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { memberSchema, type Member } from "@/lib/schemas";
import { markReady } from "@/lib/realtime/registry";

type State = { members: Member[]; loading: boolean; error: string | null };

const cache = new Map<string, Member[]>();
export const membersKey = (hid: string) => `members:${hid}`;

export function useMembers(householdId: string | undefined): State {
  const cached = householdId ? cache.get(householdId) : undefined;
  const [state, setState] = useState<State>({
    members: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const unsub = onSnapshot(
      refCol(db, paths.members(householdId)),
      (snap) => {
        const members = snap.docs.flatMap((d) => {
          const parsed = memberSchema.safeParse({
            userId: d.id,
            ...d.data(),
          });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(householdId, members);
        markReady(membersKey(householdId));
        setState({ members, loading: false, error: null });
      },
      (err) => setState({ members: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId]);

  return state;
}

export function useChildMembers(householdId: string | undefined): State {
  const all = useMembers(householdId);
  return { ...all, members: all.members.filter((m) => m.role === "child") };
}
